import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLocationDot,
  faSearch,
  faMapMarkerAlt,
  faExclamationCircle
} from '@fortawesome/free-solid-svg-icons';
import Button from './Button';
import {
  validateZipCode,
  validateRadius,
  saveLocationPreferences,
  loadLocationPreferences,
  getDefaultLocation,
  formatLocationDisplay,
  US_STATES,
  RADIUS_OPTIONS
} from '../services/locationService';

/**
 * Location setup component for entering zip code and search radius
 */
function LocationSetup({ onLocationSaved, initialLocation = null, showTitle = true }) {
  const [location, setLocation] = useState(getDefaultLocation());
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  // Load saved location on mount
  useEffect(() => {
    if (!hasLoaded) {
      const saved = initialLocation || loadLocationPreferences();
      if (saved) {
        setLocation(saved);
      }
      setHasLoaded(true);
    }
  }, [initialLocation, hasLoaded]);

  const validateForm = () => {
    const newErrors = {};

    if (!location.zipCode.trim()) {
      newErrors.zipCode = 'Zip code is required';
    } else if (!validateZipCode(location.zipCode)) {
      newErrors.zipCode = 'Please enter a valid zip code (e.g., 12345 or 12345-6789)';
    }

    if (!validateRadius(location.radius)) {
      newErrors.radius = 'Radius must be between 1 and 500 miles';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setLocation(prev => ({ ...prev, [field]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const savedLocation = saveLocationPreferences(location);
      if (savedLocation && onLocationSaved) {
        onLocationSaved(savedLocation);
      }
    } catch {
      setErrors({ general: 'Failed to save location preferences. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-lg p-6">
      {showTitle && (
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <FontAwesomeIcon icon={faLocationDot} className="h-6 w-6 text-primary-500 mr-3" />
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white">
              Set Your Location
            </h2>
          </div>
          <p className="text-neutral-600 dark:text-neutral-300">
            Enter your zip code and search radius to find adoptable pets in your area.
          </p>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Zip Code */}
        <div>
          <label htmlFor="zipCode" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Zip Code *
          </label>
          <div className="relative">
            <FontAwesomeIcon 
              icon={faMapMarkerAlt} 
              className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-neutral-400"
            />
            <input
              id="zipCode"
              type="text"
              value={location.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              placeholder="12345 or 12345-6789"
              className={`w-full pl-10 pr-3 py-2 border rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                errors.zipCode 
                  ? 'border-red-500 dark:border-red-400' 
                  : 'border-neutral-300 dark:border-neutral-600'
              }`}
              maxLength={10}
            />
          </div>
          {errors.zipCode && (
            <div className="flex items-center mt-1 text-red-600 dark:text-red-400 text-sm">
              <FontAwesomeIcon icon={faExclamationCircle} className="h-3 w-3 mr-1" />
              {errors.zipCode}
            </div>
          )}
        </div>

        {/* City and State (Optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              City (Optional)
            </label>
            <input
              id="city"
              type="text"
              value={location.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="Your city"
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <label htmlFor="state" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
              State (Optional)
            </label>
            <select
              id="state"
              value={location.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            >
              {US_STATES.map(state => (
                <option key={state.value} value={state.value}>
                  {state.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Search Radius */}
        <div>
          <label htmlFor="radius" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            Search Radius *
          </label>
          <select
            id="radius"
            value={location.radius}
            onChange={(e) => handleInputChange('radius', parseInt(e.target.value))}
            className={`w-full px-3 py-2 border rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
              errors.radius 
                ? 'border-red-500 dark:border-red-400' 
                : 'border-neutral-300 dark:border-neutral-600'
            }`}
          >
            {RADIUS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {errors.radius && (
            <div className="flex items-center mt-1 text-red-600 dark:text-red-400 text-sm">
              <FontAwesomeIcon icon={faExclamationCircle} className="h-3 w-3 mr-1" />
              {errors.radius}
            </div>
          )}
        </div>

        {/* Preview */}
        {location.zipCode && (
          <div className="bg-primary-50 dark:bg-primary-900 rounded-lg p-3">
            <p className="text-sm text-primary-700 dark:text-primary-200">
              <strong>Search area:</strong> {formatLocationDisplay(location)}
            </p>
          </div>
        )}

        {/* Error Message */}
        {errors.general && (
          <div className="bg-red-50 dark:bg-red-900 rounded-lg p-3">
            <div className="flex items-center text-red-700 dark:text-red-200">
              <FontAwesomeIcon icon={faExclamationCircle} className="h-4 w-4 mr-2" />
              {errors.general}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <Button
          type="submit"
          variant="primary"
          loading={loading}
          disabled={loading || !location.zipCode.trim()}
          className="w-full"
        >
          <FontAwesomeIcon icon={faSearch} className="mr-2" />
          {loading ? 'Saving Location...' : 'Save Location & Search for Pets'}
        </Button>
      </form>
    </div>
  );
}

LocationSetup.propTypes = {
  onLocationSaved: PropTypes.func,
  initialLocation: PropTypes.object,
  showTitle: PropTypes.bool,
};

export default LocationSetup;